'use client';

import usePusherSubscribe from '@hooks/usePusherSubscribe';
import { objectToFormData } from 'helpers/api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import type { CaptureDocument, CaptureSession, ScanRequestResult } from './types';
import useApiRequest from './useApiRequest';

/** How many files upload at once. One request per file — API Gateway caps a Lambda call at ~6MB. */
const UPLOAD_CONCURRENCY = 3;

/** Fallback poll while anything is still extracting, for when the websocket never arrives. */
const POLL_INTERVAL_MS = 4000;


interface Options {
    clubSlug: string;
    /** Skips creating a session until the window is actually open. */
    enabled?: boolean;
}

export interface UseCaptureSession {
    session: CaptureSession | null;
    documents: CaptureDocument[];
    isStarting: boolean;
    /** Files handed to the backend but not yet acknowledged, so the strip can show them. */
    pendingUploads: { uid: string; name: string }[];
    uploadFiles: (files: File[]) => Promise<void>;
    discardDocument: (documentId: number) => Promise<void>;
    retryDocument: (documentId: number) => Promise<void>;
    markConfirmed: (documentId: number) => void;
    requestScan: () => Promise<ScanRequestResult>;
    closeSession: () => Promise<void>;
    reload: () => Promise<void>;
}

/**
 * Owns one capture session: its documents, the uploads feeding it, and the two channels that
 * report progress.
 *
 * The websocket carries signals only — a document id and its status — so every signal is
 * answered with an authenticated read. A slow poll runs alongside it, because a blocked
 * websocket should make the stack slower, not broken.
 */
export const useCaptureSession = ({ clubSlug, enabled = true }: Options): UseCaptureSession => {
    const api = useApiRequest();
    const [session, setSession] = useState<CaptureSession | null>(null);
    const [documents, setDocuments] = useState<CaptureDocument[]>([]);
    const [isStarting, setIsStarting] = useState(false);
    const [pendingUploads, setPendingUploads] = useState<{ uid: string; name: string }[]>([]);
    const startedRef = useRef(false);

    const basePath = `invoice-external/${clubSlug}/capture`;

    const applySession = useCallback((data: CaptureSession | null) => {
        if (!data) return;
        setSession(data);
    }, []);

    // One session per open window. The ref guards against React 18 double-mounting in dev,
    // which would otherwise close the session it had just created.
    useEffect(() => {
        if (!enabled || startedRef.current) return;
        startedRef.current = true;
        setIsStarting(true);

        void api({
            url: `${basePath}/session`,
            method: 'POST',
            onSuccess: ({ data }) => applySession(data),
            onFinish: () => setIsStarting(false)
        });
    }, [api, applySession, basePath, enabled]);

    const anyProcessing = useMemo(
        () => documents.some((doc) => 'pending' === doc.status || 'processing' === doc.status),
        [documents]
    );

    /**
     * The stack, and the only source of it.
     *
     * Deliberately not the session's own documents: a receipt outlives the session it arrived in,
     * so closing this window — or opening it again, which starts a fresh session — must not make
     * work disappear. It is also what lets a phone scan on its own show up here at all.
     */
    const { mutate: revalidateDocuments } = useSWR<{ data: CaptureDocument[] }>(
        enabled ? `${basePath}/documents` : null,
        {
            refreshInterval: anyProcessing || pendingUploads.length > 0 ? POLL_INTERVAL_MS : 0,
            // The endpoint answers newest first; the stack reads top down in arrival order.
            onSuccess: (response) => setDocuments([...(response?.data ?? [])].sort((a, b) => a.sort_order - b.sort_order))
        }
    );

    // Fallback only, and now just for the pairing state: refreshInterval is 0 unless something
    // is actually in flight.
    const { mutate: revalidateSession } = useSWR<{ data: CaptureSession }>(
        session ? `${basePath}/session/${session.id}` : null,
        {
            refreshInterval: anyProcessing || pendingUploads.length > 0 ? POLL_INTERVAL_MS : 0,
            onSuccess: (response) => applySession(response?.data ?? null)
        }
    );

    const fetchDocument = useCallback(
        (documentId: number) =>
            api({
                url: `${basePath}/documents/${documentId}`,
                method: 'GET',
                muted: true,
                onSuccess: ({ data }: { data: CaptureDocument }) => {
                    setDocuments((prev) => {
                        const index = prev.findIndex((doc) => doc.id === data.id);
                        if (index < 0) return [...prev, data].sort((a, b) => a.sort_order - b.sort_order);
                        const next = [...prev];
                        next[index] = data;
                        return next;
                    });
                }
            }),
        [api, basePath]
    );

    usePusherSubscribe(
        session?.channel ?? null,
        useCallback(
            (event: string, payload: any) => {
                switch (event) {
                    case 'capture.document.created':
                    case 'capture.document.status':
                        // The payload is a signal; the document itself comes over the API.
                        if (payload?.document_id) void fetchDocument(payload.document_id);
                        break;
                    case 'capture.scanner.connected':
                        setSession((prev) =>
                            prev
                                ? {
                                      ...prev,
                                      scanner_connected_at: payload?.at ?? prev.scanner_connected_at ?? null,
                                      scanner_disconnected_at: null
                                  }
                                : prev
                        );
                        break;
                    // Finishing is a departure too — the scanner screen closes right after it.
                    case 'capture.scanner.disconnected':
                    case 'capture.scanner.finished':
                        setSession((prev) =>
                            prev
                                ? { ...prev, scanner_disconnected_at: payload?.at ?? new Date().toISOString() }
                                : prev
                        );
                        break;
                    case 'capture.session.closed':
                        setSession((prev) => (prev ? { ...prev, closed_at: payload?.at ?? null } : prev));
                        break;
                    default:
                        break;
                }
            },
            [fetchDocument]
        )
    );

    const uploadFiles = useCallback(
        async (files: File[]) => {
            if (!session || files.length === 0) return;

            const queued = files.map((file) => ({
                uid: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
                name: file.name,
                file
            }));
            setPendingUploads((prev) => [...prev, ...queued.map(({ uid, name }) => ({ uid, name }))]);

            const remaining = [...queued];

            const worker = async () => {
                for (;;) {
                    const next = remaining.shift();
                    if (!next) return;

                    await api({
                        url: `${basePath}/session/${session.id}/documents`,
                        method: 'POST',
                        data: objectToFormData({ file: next.file }),
                        onSuccess: ({ data }: { data: CaptureDocument }) => {
                            setDocuments((prev) =>
                                prev.some((doc) => doc.id === data.id)
                                    ? prev
                                    : [...prev, data].sort((a, b) => a.sort_order - b.sort_order)
                            );
                        },
                        onFinish: () => setPendingUploads((prev) => prev.filter((item) => item.uid !== next.uid))
                    });
                }
            };

            await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queued.length) }, worker));

            void revalidateDocuments();
        },
        [api, basePath, revalidateDocuments, session]
    );

    const discardDocument = useCallback(
        async (documentId: number) => {
            await api({
                url: `${basePath}/documents/${documentId}`,
                method: 'DELETE',
                onSuccess: () => {
                    setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
                    void revalidateDocuments();
                }
            });
        },
        [api, basePath, revalidateDocuments]
    );

    const retryDocument = useCallback(
        async (documentId: number) => {
            await api({
                url: `${basePath}/documents/${documentId}/retry`,
                method: 'POST',
                onSuccess: ({ data }: { data: CaptureDocument }) => {
                    setDocuments((prev) => prev.map((doc) => (doc.id === data.id ? data : doc)));
                    void revalidateDocuments();
                }
            });
        },
        [api, basePath, revalidateDocuments]
    );

    /** Local only: the store call already flipped the row, and refetching it would just flicker. */
    const markConfirmed = useCallback((documentId: number) => {
        setDocuments((prev) => prev.map((doc) => (doc.id === documentId ? { ...doc, status: 'confirmed' } : doc)));
    }, []);

    const requestScan = useCallback(async (): Promise<ScanRequestResult> => {
        if (!session) return { sent: false, devices: 0, reason: 'push_failed' };

        const response = await api<ScanRequestResult>({
            url: `${basePath}/session/${session.id}/scan-request`,
            method: 'POST'
        });

        setSession((prev) => (prev ? { ...prev, scan_requested_at: new Date().toISOString() } : prev));

        // A null response means the request itself failed, which for the user is the same thing
        // as the push not going out.
        if (!response) return { sent: false, devices: 0, reason: 'push_failed' };

        return {
            sent: Boolean(response.sent),
            devices: Number(response.devices ?? 0),
            reason: response.reason
        };
    }, [api, basePath, session]);

    const closeSession = useCallback(async () => {
        if (!session) return;
        await api({ url: `${basePath}/session/${session.id}`, method: 'DELETE', muted: true });
    }, [api, basePath, session]);

    /** Re-reads the stack and the session, for callers that changed something out of band. */
    const reload = useCallback(async () => {
        await Promise.all([revalidateDocuments(), revalidateSession()]);
    }, [revalidateDocuments, revalidateSession]);

    return {
        session,
        documents,
        isStarting,
        pendingUploads,
        uploadFiles,
        discardDocument,
        retryDocument,
        markConfirmed,
        requestScan,
        closeSession,
        reload
    };
};

export default useCaptureSession;
